import React from "react"
import {PaymentMethodTerms, FeeTier, FeeType} from "../../../resources/billpayTerms"
import "./TieredFeeEditor.sass"

interface TieredFeeEditorProps {
	label: string
	value: PaymentMethodTerms
	onChange: (value: PaymentMethodTerms) => void
	isEditing: boolean
}

function getDefaultTier(): FeeTier {
	return {
		type: "Fixed",
		value: 0,
		range: {
			lowerBound: 0,
			upperBound: null,
		},
	}
}

function getRangeLabel(tier: FeeTier, index: number, tiers: FeeTier[]): React.ReactNode {
	const isFirst = index === 0
	const isLast = index === tiers.length - 1
	const prevTier = index > 0 ? tiers[index - 1] : null

	if (tiers.length === 1) {
		// Single tier - no range display needed
		return null
	}

	if (isFirst) {
		return <span className="range-label">Up to</span>
	}

	if (isLast) {
		const lowerBound = prevTier?.range.upperBound ?? tier.range.lowerBound
		return <span className="range-label range-label-readonly">Over ${lowerBound?.toLocaleString()}</span>
	}

	// Middle tier
	const lowerBound = prevTier?.range.upperBound ?? tier.range.lowerBound
	return (
		<>
			<span className="range-label range-label-readonly">Over ${lowerBound?.toLocaleString()} Up to</span>
		</>
	)
}

function TierRow({
	tier,
	index,
	tiers,
	isEditing,
	onUpdateTier,
	onRemoveTier,
	groupId,
}: {
	tier: FeeTier
	index: number
	tiers: FeeTier[]
	isEditing: boolean
	onUpdateTier: (index: number, updatedTier: FeeTier) => void
	onRemoveTier: (index: number) => void
	groupId: string
}) {
	const isLast = index === tiers.length - 1
	const showUpperBoundDropdown = !isLast || tiers.length === 1
	const showRemove = tiers.length > 1 && index > 0

	const handleFeeValueChange = (value: string) => {
		const numValue = value === "" ? 0 : parseFloat(value)
		onUpdateTier(index, {
			...tier,
			value: isNaN(numValue) ? 0 : numValue,
		})
	}

	const handleFeeTypeChange = (type: FeeType) => {
		onUpdateTier(index, {
			...tier,
			type,
		})
	}

	const handleUpperBoundChange = (value: string) => {
		if (value === "") {
			onUpdateTier(index, {
				...tier,
				range: {
					...tier.range,
					upperBound: null,
				},
			})
			return
		}
		const numValue = parseFloat(value)
		if (isNaN(numValue)) {
			return
		}
		// Allow typing any value - validation happens on blur
		onUpdateTier(index, {
			...tier,
			range: {
				...tier.range,
				upperBound: numValue,
			},
		})
	}

	const handleUpperBoundBlur = () => {
		// Validate on blur and correct if invalid
		if (tier.range.upperBound === null) {
			return
		}
		const prevTier = index > 0 ? tiers[index - 1] : null
		const prevUpperBound = prevTier?.range.upperBound ?? 0
		const minUpperBound = Math.max(tier.range.lowerBound, prevUpperBound) + 0.01

		if (tier.range.upperBound <= minUpperBound - 0.01) {
			// Correct to minimum valid value
			onUpdateTier(index, {
				...tier,
				range: {
					...tier.range,
					upperBound: minUpperBound,
				},
			})
		}
	}

	const rangeLabel = getRangeLabel(tier, index, tiers)

	return (
		<div className="tier-row">
			{tiers.length > 1 && (
				<div className="tier-range">
					{rangeLabel}
					{showUpperBoundDropdown && (
						<div className="field has-addons upper-bound-field">
							<p className="control">
								<span className="button is-static is-small">$</span>
							</p>
							<div className="control">
								<input
									className="input is-small upper-bound-input"
									type="number"
									step="0.01"
									min={(() => {
										if (index > 0) {
											const prevUpperBound = tiers[index - 1].range.upperBound
											if (prevUpperBound !== null) {
												return Math.max(tier.range.lowerBound, prevUpperBound) + 0.01
											}
										}
										return tier.range.lowerBound + 0.01
									})()}
									value={tier.range.upperBound ?? ""}
									onChange={(e) => handleUpperBoundChange(e.target.value)}
									onBlur={handleUpperBoundBlur}
									disabled={!isEditing}
									placeholder="Amount"
								/>
							</div>
						</div>
					)}
				</div>
			)}

			<div className="tier-fee">
				<span className="fee-label">Fee:</span>
				<div className="field has-addons">
					<p className="control">
						<span className="button is-static is-small">{tier.type === "Percentage" ? "%" : "$"}</span>
					</p>
					<div className="control">
						<input
							className="input is-small"
							type="number"
							step="0.01"
							min="0"
							value={tier.value || ""}
							onChange={(e) => handleFeeValueChange(e.target.value)}
							disabled={!isEditing}
							placeholder="0"
						/>
					</div>
				</div>
			</div>

			<div className="tier-type">
				<label className="radio">
					<input
						type="radio"
						name={`fee-type-${groupId}-${index}`}
						checked={tier.type === "Percentage"}
						onChange={() => handleFeeTypeChange("Percentage")}
						disabled={!isEditing}
					/>
					<span>%</span>
				</label>
				<label className="radio">
					<input
						type="radio"
						name={`fee-type-${groupId}-${index}`}
						checked={tier.type === "Fixed"}
						onChange={() => handleFeeTypeChange("Fixed")}
						disabled={!isEditing}
					/>
					<span>$</span>
				</label>
			</div>

			{showRemove && isEditing && (
				<button className="button-remove" onClick={() => onRemoveTier(index)} type="button">
					Remove
				</button>
			)}
		</div>
	)
}

export default function TieredFeeEditor({label, value, onChange, isEditing}: TieredFeeEditorProps) {
	const tiers = value.fee?.tiers ?? [getDefaultTier()]

	const handleAllowedChange = (allowed: boolean) => {
		if (allowed) {
			// When enabling, ensure we have at least one tier
			onChange({
				allowed,
				fee: value.fee ?? {tiers: [getDefaultTier()]},
			})
		} else {
			onChange({
				allowed,
				fee: null,
			})
		}
	}

	const handleUpdateTier = (index: number, updatedTier: FeeTier) => {
		const newTiers = [...tiers]
		newTiers[index] = updatedTier

		// Cascade updates to subsequent tiers when upper bound changes
		// Update all subsequent tiers to ensure valid ranges
		for (let i = index + 1; i < newTiers.length; i++) {
			const currentTier = newTiers[i]
			const prevTier = newTiers[i - 1]
			const prevUpperBound = prevTier.range.upperBound ?? 0
			const newLowerBound = prevUpperBound
			const currentUpperBound = currentTier.range.upperBound

			// Always update lower bound to match previous tier's upper bound
			// If upper bound is invalid (<= lower bound), update it too
			const updatedUpperBound =
				currentUpperBound !== null && currentUpperBound <= newLowerBound ? newLowerBound + 0.01 : currentUpperBound

			newTiers[i] = {
				...currentTier,
				range: {
					...currentTier.range,
					lowerBound: newLowerBound,
					upperBound: updatedUpperBound,
				},
			}
		}

		onChange({
			...value,
			fee: {tiers: newTiers},
		})
	}

	const handleRemoveTier = (index: number) => {
		const newTiers = tiers.filter((_, i) => i !== index)

		// Update lower bounds after removal
		for (let i = 1; i < newTiers.length; i++) {
			newTiers[i] = {
				...newTiers[i],
				range: {
					...newTiers[i].range,
					lowerBound: newTiers[i - 1].range.upperBound ?? 0,
				},
			}
		}

		// Make sure the last tier has no upper bound
		if (newTiers.length > 0) {
			newTiers[newTiers.length - 1] = {
				...newTiers[newTiers.length - 1],
				range: {
					...newTiers[newTiers.length - 1].range,
					upperBound: null,
				},
			}
		}

		onChange({
			...value,
			fee: {tiers: newTiers},
		})
	}

	const handleAddTier = () => {
		const lastTier = tiers[tiers.length - 1]

		// Calculate a sensible default for the new upper bound
		const lastUpperBound = lastTier.range.upperBound ?? 0
		// Default to the last upper bound + 1000, or 1000 if no upper bound exists
		const nextUpperBound = lastUpperBound > 0 ? lastUpperBound + 1000 : 1000

		// Update the last tier to have an upper bound
		const updatedLastTier: FeeTier = {
			...lastTier,
			range: {
				...lastTier.range,
				upperBound: nextUpperBound,
			},
		}

		// Create new tier with lower bound matching the previous upper bound
		const newTier: FeeTier = {
			type: "Fixed",
			value: 0,
			range: {
				lowerBound: nextUpperBound,
				upperBound: null,
			},
		}

		const newTiers = [...tiers.slice(0, -1), updatedLastTier, newTier]

		onChange({
			...value,
			fee: {tiers: newTiers},
		})
	}

	return (
		<div className="tiered-fee-section">
			<div className="tiered-fee-header">
				<span className="method-label">{label}</span>
				<label className="checkbox">
					<input
						type="checkbox"
						checked={value.allowed}
						onChange={(e) => handleAllowedChange(e.target.checked)}
						disabled={!isEditing}
					/>
					<span>Enable</span>
				</label>
			</div>

			{value.allowed && (
				<div className="tiered-fee-content">
					{tiers.map((tier, index) => (
						<TierRow
							key={index}
							tier={tier}
							index={index}
							tiers={tiers}
							isEditing={isEditing}
							onUpdateTier={handleUpdateTier}
							onRemoveTier={handleRemoveTier}
							groupId={label.replace(/\s+/g, "-").toLowerCase()}
						/>
					))}

					{isEditing && (
						<button className="button-add-tier" onClick={handleAddTier} type="button">
							+ Add tier
						</button>
					)}
				</div>
			)}
		</div>
	)
}
